import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  AmortizationRunRow,
  AmortizePrepaid,
  AmortizePreview,
  AmortizePreviewResult,
  AmortizeResult,
  CreateOpeningBalance,
  CreatePrepaidContract,
  ExpenseDirect,
  ModifyPrepaid,
  OpeningBalanceTieout,
  PrepaidAmortizationEntry,
  PrepaidContractListRow,
  PrepaidContractWithEntries,
  PrepaidHistoryEvent,
  PrepaidReconExceptionsResult,
  PrepaidRollforwardResult,
  PrepaidRunEntity,
  PrepaidScheduleRow,
  PrepaidSettings,
  PrepaidStatus,
  SuspendPrepaid,
  TerminatePrepaid,
  UpdatePrepaidContract,
} from '@dcc-erp/shared';

import { api, withSodOverride } from '../api';

// Cache-key scopes — keyed by companyId so DCC/TTOP/RCI switches give
// independent caches (same pattern as ar.ts / journal-entries.ts).
const contractsKey = (companyId: string | undefined) => ['prepaid-contracts', companyId] as const;

export interface PrepaidFilters {
  status?: PrepaidStatus;
  expenseAccountCode?: string;
  branchId?: string;
  departmentId?: string;
}

export function usePrepaidContracts(companyId: string | undefined, filters: PrepaidFilters = {}) {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.expenseAccountCode) qs.set('expenseAccountCode', filters.expenseAccountCode);
  if (filters.branchId) qs.set('branchId', filters.branchId);
  if (filters.departmentId) qs.set('departmentId', filters.departmentId);
  const search = qs.toString();
  return useQuery({
    queryKey: [...contractsKey(companyId), filters],
    queryFn: () =>
      api<PrepaidContractListRow[]>(
        `/companies/${companyId}/prepaid-contracts${search ? `?${search}` : ''}`,
      ),
    enabled: !!companyId,
  });
}

export function usePrepaidContract(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['prepaid-contracts', companyId, id],
    queryFn: () =>
      api<PrepaidContractWithEntries>(`/companies/${companyId}/prepaid-contracts/${id}`),
    enabled: !!companyId && !!id,
  });
}

export function usePrepaidAmortization(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['prepaid-contracts', companyId, id, 'amortization'],
    queryFn: () =>
      api<PrepaidAmortizationEntry[]>(
        `/companies/${companyId}/prepaid-contracts/${id}/amortization`,
      ),
    enabled: !!companyId && !!id,
  });
}

export function useCreatePrepaidContract(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      data,
      idempotencyKey,
    }: {
      data: CreatePrepaidContract;
      idempotencyKey?: string;
    }) =>
      api<PrepaidContractWithEntries>(`/companies/${companyId}/prepaid-contracts`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contractsKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
      void qc.invalidateQueries({ queryKey: ['dashboard', companyId] });
    },
  });
}

// Run history for the Journal-history tab — one row per batched amortization JV.
export function useAmortizationRuns(companyId: string | undefined) {
  return useQuery({
    queryKey: ['prepaid-amortization-runs', companyId],
    queryFn: () =>
      api<AmortizationRunRow[]>(`/companies/${companyId}/prepaid-contracts/amortization-runs`),
    enabled: !!companyId,
  });
}

// ============================================================
// Lifecycle (P6) — terminate+write-off, modify/re-spread, suspend/resume,
// expense-direct, settings. All bust the contract list + journal/dashboard.
// ============================================================

function invalidateLifecycle(qc: ReturnType<typeof useQueryClient>, companyId: string | undefined) {
  void qc.invalidateQueries({ queryKey: contractsKey(companyId) });
  void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
  void qc.invalidateQueries({ queryKey: ['dashboard', companyId] });
}

export function useTerminatePrepaidContract(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TerminatePrepaid }) =>
      api(`/companies/${companyId}/prepaid-contracts/${id}/terminate`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => invalidateLifecycle(qc, companyId),
  });
}

export function useModifyPrepaidContract(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ModifyPrepaid }) =>
      api(`/companies/${companyId}/prepaid-contracts/${id}/modify`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (_r, { id }) => {
      invalidateLifecycle(qc, companyId);
      void qc.invalidateQueries({ queryKey: ['prepaid-contracts', companyId, id, 'schedule'] });
    },
  });
}

export function useSuspendPrepaidContract(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SuspendPrepaid }) =>
      api(`/companies/${companyId}/prepaid-contracts/${id}/suspend`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => invalidateLifecycle(qc, companyId),
  });
}

export function useResumePrepaidContract(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api(`/companies/${companyId}/prepaid-contracts/${id}/resume`, { method: 'POST' }),
    onSuccess: () => invalidateLifecycle(qc, companyId),
  });
}

export function useExpenseDirect(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ data, idempotencyKey }: { data: ExpenseDirect; idempotencyKey?: string }) =>
      api(`/companies/${companyId}/prepaid-contracts/expense-direct`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        body: JSON.stringify(data),
      }),
    onSuccess: () => invalidateLifecycle(qc, companyId),
  });
}

export function usePrepaidSettings(companyId: string | undefined) {
  return useQuery({
    queryKey: ['prepaid-settings', companyId],
    queryFn: () =>
      api<PrepaidSettings>(`/companies/${companyId}/prepaid-contracts/prepaid-settings`),
    enabled: !!companyId,
  });
}

export function useAmortizePrepaid(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      data,
      idempotencyKey,
    }: {
      data:
        | AmortizePrepaid
        | {
            period?: string;
            fromPeriod?: string;
            toPeriod?: string;
            excludeContractIds?: string[];
          };
      idempotencyKey?: string;
    }) =>
      api<AmortizeResult>(`/companies/${companyId}/prepaid-contracts/amortize`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contractsKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
      void qc.invalidateQueries({ queryKey: ['dashboard', companyId] });
      void qc.invalidateQueries({ queryKey: ['prepaid-amortization-runs', companyId] });
    },
  });
}

export function useAmortizePreview(companyId: string | undefined) {
  return useMutation({
    mutationFn: (data: AmortizePreview) =>
      api<AmortizePreviewResult>(`/companies/${companyId}/prepaid-contracts/amortize/preview`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  });
}

export function usePrepaidSchedule(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['prepaid-contracts', companyId, id, 'schedule'],
    queryFn: () =>
      api<PrepaidScheduleRow[]>(`/companies/${companyId}/prepaid-contracts/${id}/schedule`),
    enabled: !!companyId && !!id,
  });
}

// Contract change/lifecycle history (P7) — modify field-diffs + terminate/suspend.
export function usePrepaidHistory(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['prepaid-contracts', companyId, id, 'history'],
    queryFn: () =>
      api<PrepaidHistoryEvent[]>(`/companies/${companyId}/prepaid-contracts/${id}/history`),
    enabled: !!companyId && !!id,
  });
}

// P8 — roll-forward + GL recon for a period (optional branch/dept scope).
export interface RollforwardFilters {
  branchId?: string;
  departmentId?: string;
}

export function usePrepaidRollforward(
  companyId: string | undefined,
  period: string,
  filters: RollforwardFilters = {},
) {
  const qs = new URLSearchParams({ period });
  if (filters.branchId) qs.set('branchId', filters.branchId);
  if (filters.departmentId) qs.set('departmentId', filters.departmentId);
  return useQuery({
    queryKey: [
      'prepaid-rollforward',
      companyId,
      period,
      filters.branchId ?? null,
      filters.departmentId ?? null,
    ],
    queryFn: () =>
      api<PrepaidRollforwardResult>(
        `/companies/${companyId}/prepaid-contracts/rollforward?${qs.toString()}`,
      ),
    enabled: !!companyId && !!period,
  });
}

// P8 — reconciliation exception worklist (lazy: only fetched when `enabled`,
// e.g. when the user expands the worklist behind a red variance light).
export function usePrepaidReconExceptions(
  companyId: string | undefined,
  period: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['prepaid-recon-exceptions', companyId, period],
    queryFn: () =>
      api<PrepaidReconExceptionsResult>(
        `/companies/${companyId}/prepaid-contracts/recon-exceptions?period=${period}`,
      ),
    enabled: enabled && !!companyId && !!period,
  });
}

export function useCreateOpeningBalance(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      data,
      idempotencyKey,
    }: {
      data: CreateOpeningBalance;
      idempotencyKey?: string;
    }) =>
      api<PrepaidContractWithEntries>(`/companies/${companyId}/prepaid-contracts/opening-balance`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contractsKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['prepaid-tieout', companyId] });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

export function useOpeningBalanceTieout(companyId: string | undefined) {
  return useQuery({
    queryKey: ['prepaid-tieout', companyId],
    queryFn: () =>
      api<OpeningBalanceTieout>(`/companies/${companyId}/prepaid-contracts/opening-balance-tieout`),
    enabled: !!companyId,
  });
}

export function useUpdatePrepaidContract(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePrepaidContract }) =>
      api<PrepaidContractListRow>(`/companies/${companyId}/prepaid-contracts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: contractsKey(companyId) }),
  });
}

// ============================================================
// Amortization-run governance — entity list + detail + approve/reject/reverse.
// (The run is a first-class approval-engine doc; see prepaid-run.approval-adapter.)
// ============================================================

const runEntitiesKey = (companyId: string | undefined) =>
  ['prepaid-run-entities', companyId] as const;

// Shared cache-busting after any run-state change (approve/reject/reverse).
function invalidateRunGovernance(
  qc: ReturnType<typeof useQueryClient>,
  companyId: string | undefined,
) {
  void qc.invalidateQueries({ queryKey: runEntitiesKey(companyId) });
  void qc.invalidateQueries({ queryKey: ['prepaid-amortization-runs', companyId] });
  void qc.invalidateQueries({ queryKey: contractsKey(companyId) });
  void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
  void qc.invalidateQueries({ queryKey: ['dashboard', companyId] });
}

// Governance list — PENDING_APPROVAL / POSTED / REVERSED run entities (+chain).
export function useAmortizationRunEntities(companyId: string | undefined) {
  return useQuery({
    queryKey: runEntitiesKey(companyId),
    queryFn: () =>
      api<PrepaidRunEntity[]>(
        `/companies/${companyId}/prepaid-contracts/amortization-runs/governance`,
      ),
    enabled: !!companyId,
  });
}

// Single run + approval chain (for the timeline in the decision panel).
export function useAmortizationRun(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: [...runEntitiesKey(companyId), id],
    queryFn: () =>
      api<PrepaidRunEntity>(`/companies/${companyId}/prepaid-contracts/amortization-runs/${id}`),
    enabled: !!companyId && !!id,
  });
}

export function useApproveAmortizationRun(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      comment,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      comment?: string;
      sodOverrideReason?: string;
    }) =>
      api<PrepaidRunEntity>(
        `/companies/${companyId}/prepaid-contracts/amortization-runs/${id}/approve`,
        {
          method: 'POST',
          body: JSON.stringify(
            withSodOverride(
              { step, ...(comment !== undefined ? { comment } : {}) },
              sodOverrideReason,
            ),
          ),
        },
      ),
    onSuccess: () => invalidateRunGovernance(qc, companyId),
  });
}

export function useRejectAmortizationRun(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      reason,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      reason: string;
      sodOverrideReason?: string;
    }) =>
      api<PrepaidRunEntity>(
        `/companies/${companyId}/prepaid-contracts/amortization-runs/${id}/reject`,
        {
          method: 'POST',
          body: JSON.stringify(withSodOverride({ step, reason }, sodOverrideReason)),
        },
      ),
    onSuccess: () => invalidateRunGovernance(qc, companyId),
  });
}

export function useReverseAmortizationRun(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api<{ status: string; reversalJournalEntryId: string | null }>(
        `/companies/${companyId}/prepaid-contracts/amortization-runs/${id}/reverse`,
        { method: 'POST', body: JSON.stringify({ reason }) },
      ),
    onSuccess: () => invalidateRunGovernance(qc, companyId),
  });
}
